import { useEffect } from 'react';

import { useTheme } from '@mui/material';
import { drawFramedSigPreview } from 'src/layout-engine/components/dialogs/signature-canvas-utils';

interface UseFramedPreviewOptions {
  canvas: HTMLCanvasElement | null;
  fallbackText: string;
  hPx: number;
  textOrDataUrl: string;
  type: 'text' | 'dataUrl';
  wPx: number;
  /**
   * Skip rendering even when the canvas is mounted. Used by the desktop
   * dialog to keep typed previews stable while the user is on the Draw tab.
   */
  enabled?: boolean;
}

/**
 * Rasterizes the framed "Signed via Aline" preview onto `canvas` whenever the
 * source text/dataUrl or dimensions change. Shared by the desktop adoption
 * dialog and the mobile adoption sheet so adopted signatures look identical
 * regardless of where the user adopts them.
 */
export function useFramedPreview({
  canvas,
  enabled = true,
  fallbackText,
  hPx,
  textOrDataUrl,
  type,
  wPx,
}: UseFramedPreviewOptions): void {
  const theme = useTheme();
  const placeholderColor = theme.appColors.placeholderType;

  useEffect(() => {
    if (!canvas || !enabled) return;
    function redraw() {
      if (!canvas) return;
      void drawFramedSigPreview(canvas, {
        fallbackText,
        hPx,
        placeholderColor,
        previewInfo: { textOrDataUrl, type },
        wPx,
      });
    }
    redraw();
    /**
     * visualViewport.resize fires on pinch zoom; redraw so the HiDPI canvas
     * stays sharp at the new scale (see createHiResCtx).
     */
    window.visualViewport?.addEventListener('resize', redraw);
    return () => window.visualViewport?.removeEventListener('resize', redraw);
  }, [canvas, enabled, fallbackText, hPx, placeholderColor, textOrDataUrl, type, wPx]);
}
